import React, { useEffect, useMemo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore, useCollectionStore, useUIStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import { MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS } from '@/constants/config';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';

const createStyles = (isXS, messageWidth) => StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW
      : messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM
      : '100%',
    alignSelf: 'center',
    alignItems: 'center',
    paddingHorizontal: isXS ? 15 : 20,
    flexShrink: 0,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minHeight: 44,
    maxWidth: '100%',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  title: {
    flexShrink: 1,
    minWidth: 0,
  },
});

// Collection membership is useful even while typing or generating a reply.
// Subscribe to the collection itself so renames and moves update immediately.
function ChatCollectionBadge({ collectionId }) {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const token = useAuthStore(state => state.token);
  const collection = useCollectionStore(state => state.collections[collectionId]);
  const fetchCollectionById = useCollectionStore(state => state.fetchCollectionById);
  const messageWidth = useUIStore(state => state.messageWidth);
  const styles = useMemo(() => createStyles(isXS, messageWidth), [isXS, messageWidth]);

  useEffect(() => {
    // Deep-linked chats can belong to a collection beyond the sidebar's first
    // page. Resolve that collection through the existing authenticated store.
    if (!token || !collectionId || collection) return;
    fetchCollectionById(collectionId).catch(() => {});
  }, [token, collectionId, collection, fetchCollectionById]);

  if (!collectionId || !collection) return null;
  const title = collection.title || t('collections.defaultName');

  return (
    <View style={styles.container}>
      <Pressable
        testID="chat-collection-badge"
        accessibilityRole="link"
        accessibilityLabel={`${t('dropdowns.viewCollection')}: ${title}`}
        onPress={() => router.push(`/collections/${collectionId}`)}
        style={({ pressed }) => [styles.badge, pressed && { opacity: 0.7 }]}
      >
        <Icon name="folder" size={12} color={colors.text_color_tertiary} />
        <Text size="xs" color="tertiary" numberOfLines={1} style={styles.title}>
          {title}
        </Text>
      </Pressable>
    </View>
  );
}

export default React.memo(ChatCollectionBadge);
